// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Drawer, Group, Paper, ScrollArea, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import type { Appointment } from '@medplum/fhirtypes';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppointmentDetailsModal } from '../../components/schedule/AppointmentDetailsModal';
import { AppointmentsCalendarCard } from '../../components/schedule/AppointmentsCalendarCard';
import { CreateVisit } from '../../components/schedule/CreateVisit';
import { RecentAppointmentsTable } from '../../components/schedule/RecentAppointmentsTable';
import { SelectPatientDrawer } from '../../components/schedule/SelectPatientDrawer';
import { TodaysAppointmentsTable } from '../../components/schedule/TodaysAppointmentsTable';
import { useAppointmentDetails } from '../../hooks/useAppointmentDetails';
import type { ProviderAppointmentDetails } from '../../hooks/useProviderAppointmentDetails';
import { useProviderAppointmentDetails } from '../../hooks/useProviderAppointmentDetails';
import { useProviderAppointments } from '../../hooks/useProviderAppointments';
import { useRole } from '../../hooks/useRole';
import { formatAppointmentDate, getAppointmentStatus } from '../../utils/appointment-formatting';
import { canViewClinicAppointments } from '../../utils/permissions';
import classes from './SchedulePage.module.css';

const scrollAreaStyles = {
  thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
};

/**
 * Schedule page that displays provider tables plus a calendar with screenshot-like
 * filter controls for appointment type, provider, and calendar range.
 */
export function SchedulePage(): JSX.Element {
  const navigate = useNavigate();
  const { role, isAdmin } = useRole();
  const clinicViewEnabled = canViewClinicAppointments({ role, isAdmin });

  const [createAppointmentOpened, createAppointmentHandlers] = useDisclosure(false);
  const [patientPickerOpened, patientPickerHandlers] = useDisclosure(false);
  const [appointmentSlot, setAppointmentSlot] = useState<unknown>();
  const [selectedCalendarAppointment, setSelectedCalendarAppointment] = useState<Appointment | undefined>();
  const [calendarRefreshToken, setCalendarRefreshToken] = useState(0);
  const [calendarAppointmentDetailsOpened, calendarAppointmentDetailsHandlers] = useDisclosure(false);
  const [selectedJoinAppointment, setSelectedJoinAppointment] = useState<ProviderAppointmentDetails | undefined>();
  const [joinModalOpened, joinModalHandlers] = useDisclosure(false);

  const [todaySearch, setTodaySearch] = useState('');
  const [recentSearch, setRecentSearch] = useState('');
  const [recentStatus, setRecentStatus] = useState('all');
  const [recentRange, setRecentRange] = useState('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [debouncedTodaySearch] = useDebouncedValue(todaySearch, 300);
  const [debouncedRecentSearch] = useDebouncedValue(recentSearch, 300);

  const {
    todayAppointments,
    recentAppointments,
    loading: appointmentsLoading,
    refresh: refreshAppointments,
  } = useProviderAppointments({
    clinicView: clinicViewEnabled,
    recentDays: recentRange === 'all' || recentRange === 'custom' ? null : Number(recentRange),
  });
  const { details: todayDetails, loading: todayEnriching } = useProviderAppointmentDetails(todayAppointments);
  const { details: recentDetails, loading: recentEnriching } = useProviderAppointmentDetails(recentAppointments);
  const selectedCalendarAppointmentList = useMemo(
    () => (selectedCalendarAppointment ? [selectedCalendarAppointment] : []),
    [selectedCalendarAppointment]
  );
  const { details: selectedCalendarAppointmentDetails } = useAppointmentDetails(selectedCalendarAppointmentList);
  const filteredRecentDetails = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let from: Date | undefined = new Date(today);
    let to: Date | undefined = new Date(today);
    if (recentRange === 'custom') {
      from = customFrom ? new Date(`${customFrom}T00:00:00`) : undefined;
      to = customTo ? new Date(`${customTo}T23:59:59.999`) : undefined;
    }
    if (recentRange === 'all') {
      from = undefined;
      to = undefined;
    }
    if (recentRange !== 'all' && recentRange !== 'custom') {
      from?.setDate(from.getDate() - Number(recentRange));
      to?.setHours(23, 59, 59, 999);
    }
    return recentDetails.filter((details) => {
      const start = details.appointment.start ? new Date(details.appointment.start) : undefined;
      const inDateRange = !start || ((!from || start >= from) && (!to || start <= to));
      const inStatus = recentStatus === 'all' || getAppointmentStatus(details.appointment) === recentStatus;
      return inDateRange && inStatus;
    });
  }, [customFrom, customTo, recentDetails, recentRange, recentStatus]);
  const pastRangeSummary = useMemo(() => {
    if (recentRange === 'all') {
      return 'all time';
    }
    if (recentRange === 'custom') {
      const from = customFrom ? formatAppointmentDate(`${customFrom}T00:00:00`) : 'the beginning';
      const to = customTo ? formatAppointmentDate(`${customTo}T00:00:00`) : 'today';
      return `${from} – ${to}`;
    }
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() - Number(recentRange));
    return `${formatAppointmentDate(from.toISOString())} – ${formatAppointmentDate(new Date().toISOString())}`;
  }, [customFrom, customTo, recentRange]);

  const handleReschedule = useCallback(
    (details: ProviderAppointmentDetails) => {
      if (details.appointment.id && details.patientId) {
        navigate(`/Patient/${details.patientId}/Appointment/reschedule/${details.appointment.id}`);
      }
    },
    [navigate]
  );

  const handleJoin = useCallback(
    (details: ProviderAppointmentDetails) => {
      setSelectedJoinAppointment(details);
      joinModalHandlers.open();
    },
    [joinModalHandlers]
  );

  return (
    <Stack gap="md" p="md" mih="100%" className={classes.page}>
      <div className={classes.pageHead}>
        <Title order={1} className={classes.pageTitle}>
          Appointments
        </Title>
      </div>

      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white" className={classes.card}>
        <Group justify="space-between" mb="sm">
          <Title order={3} size="h2">
            Today&apos;s Appointments
          </Title>
          <TextInput
            placeholder="Search"
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={220}
            value={todaySearch}
            onChange={(event) => setTodaySearch(event.currentTarget.value)}
          />
        </Group>
        <ScrollArea scrollbarSize={6} type="auto" styles={scrollAreaStyles}>
          <div style={{ minWidth: 1000, paddingBottom: '1rem' }}>
            <TodaysAppointmentsTable
              appointments={todayDetails}
              loading={appointmentsLoading || todayEnriching}
              searchQuery={debouncedTodaySearch}
              showProvider={clinicViewEnabled}
              onReschedule={handleReschedule}
              onJoin={handleJoin}
            />
          </div>
        </ScrollArea>
      </Paper>

      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white" className={classes.card}>
        <Group justify="space-between" mb="sm">
          <Title order={3} size="h2">
            Past Appointments
          </Title>
          <Group gap="sm" wrap="wrap">
            <Select
              aria-label="Past appointment date range"
              value={recentRange}
              onChange={(value) => setRecentRange(value ?? '30')}
              className={classes.filter}
              data={[
                { value: '7', label: 'Last 7 days' },
                { value: '30', label: 'Last 30 days' },
                { value: '90', label: 'Last 90 days' },
                { value: '365', label: 'Last 12 months' },
                { value: 'all', label: 'All time' },
                { value: 'custom', label: 'Custom range…' },
              ]}
              w={155}
            />
            {recentRange === 'custom' && (
              <Group gap="xs" wrap="nowrap">
                <TextInput
                  aria-label="From date"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.currentTarget.value)}
                />
                <Text c="dimmed">–</Text>
                <TextInput
                  aria-label="To date"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.currentTarget.value)}
                />
              </Group>
            )}
            <Select
              aria-label="Past appointment status"
              value={recentStatus}
              onChange={(value) => setRecentStatus(value ?? 'all')}
              className={classes.filter}
              data={[
                { value: 'all', label: 'Status' },
                { value: 'fulfilled', label: 'Completed' },
                { value: 'rescheduled', label: 'Rescheduled' },
                { value: 'cancelled', label: 'Cancelled' },
                { value: 'noshow', label: 'Patient No-show' },
                { value: 'technical-issues', label: 'Technical Issues' },
              ]}
              w={135}
            />
            <TextInput
              placeholder="Search"
              leftSection={<IconSearch size={14} />}
              size="sm"
              w={220}
              value={recentSearch}
              onChange={(event) => setRecentSearch(event.currentTarget.value)}
            />
          </Group>
        </Group>
        <ScrollArea scrollbarSize={6} type="auto" styles={scrollAreaStyles}>
          <div style={{ minWidth: 1000, paddingBottom: '1rem' }}>
            <RecentAppointmentsTable
              appointments={filteredRecentDetails}
              loading={appointmentsLoading || recentEnriching}
              searchQuery={debouncedRecentSearch}
              showProvider={clinicViewEnabled}
              emptyMessage={
                recentDetails.length === 0 ? 'No past appointments.' : 'No past appointments in this range.'
              }
              rangeSummary={pastRangeSummary}
            />
          </div>
        </ScrollArea>
      </Paper>

      <AppointmentsCalendarCard
        title={clinicViewEnabled ? 'Clinic Calendar' : 'Appointments'}
        canViewClinicAppointments={clinicViewEnabled}
        includeAvailability
        showCreateAppointment
        onCreateAppointment={() => patientPickerHandlers.open()}
        showEditAvailability
        onEditAvailability={() => navigate('/settings#availability')}
        onAppointmentSelect={(appointment) => {
          setSelectedCalendarAppointment(appointment);
          calendarAppointmentDetailsHandlers.open();
        }}
        refreshToken={calendarRefreshToken}
        onSlotSelect={(slot) => {
          createAppointmentHandlers.open();
          setAppointmentSlot(slot);
        }}
        calendarHeight={700}
      />

      <SelectPatientDrawer
        opened={patientPickerOpened}
        onClose={patientPickerHandlers.close}
        onPatientSelect={(patient) => {
          patientPickerHandlers.close();
          if (patient.id) {
            navigate(`/Patient/${patient.id}/Appointment/schedule`);
          }
        }}
      />

      <Drawer
        opened={createAppointmentOpened}
        onClose={() => {
          createAppointmentHandlers.close();
          setAppointmentSlot(undefined);
        }}
        title="New Calendar Event"
        position="right"
        h="100%"
      >
        <CreateVisit appointmentSlot={appointmentSlot as any} />
      </Drawer>

      {selectedCalendarAppointmentDetails[0] && (
        <AppointmentDetailsModal
          appointment={selectedCalendarAppointmentDetails[0]}
          opened={calendarAppointmentDetailsOpened}
          onCancelled={() => setCalendarRefreshToken((token) => token + 1)}
          onMigrated={() => {
            refreshAppointments();
            setCalendarRefreshToken((token) => token + 1);
          }}
          onClose={() => {
            calendarAppointmentDetailsHandlers.close();
            setSelectedCalendarAppointment(undefined);
          }}
        />
      )}

      {selectedJoinAppointment && (
        <AppointmentDetailsModal
          opened={joinModalOpened}
          onClose={() => {
            joinModalHandlers.close();
            setSelectedJoinAppointment(undefined);
          }}
          appointment={selectedJoinAppointment}
        />
      )}
    </Stack>
  );
}
